import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import type { InventoryLocationSelection, InventoryVariboxSnapshot } from '@/data/inventoryUtils';
import { useInventoryDemoState, type CompletedInventorySession } from '@/hooks/useInventoryDemoState';
import { useI18n } from '@/i18n';
import { BrowserMultiFormatReader, type IScannerControls } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { getLocationTree, type ApiLocationNode } from '@/services/locationApi';
import { listVariboxes, mapApiVariboxToView, type VariboxViewRecord } from '@/services/variboxApi';
import { findVariboxByBarcode } from '@/utils/variboxScan';

interface LocationOption {
  id: string;
  rootId: string;
  code: string | null;
  name: string;
  label: string;
  depth: number;
}

type CameraScanMode = 'barcode' | 'qr';

const ONE_DIMENSIONAL_FORMATS = [
  BarcodeFormat.CODABAR,
  BarcodeFormat.CODE_39,
  BarcodeFormat.CODE_93,
  BarcodeFormat.CODE_128,
  BarcodeFormat.EAN_8,
  BarcodeFormat.EAN_13,
  BarcodeFormat.ITF,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.RSS_14,
  BarcodeFormat.RSS_EXPANDED,
];

function formatLocationLabel(item: ApiLocationNode) {
  if (item.code && item.code.trim().toLowerCase() === item.name.trim().toLowerCase()) {
    return item.name.trim();
  }

  const codePart = item.code ? `${item.code} ` : '';
  return `${codePart}${item.name}`.trim();
}

function flattenLocations(nodes: ApiLocationNode[]) {
  const rootOptions: LocationOption[] = [];
  const childOptions: LocationOption[] = [];
  const locationById = new Map<string, LocationOption>();

  const walk = (items: ApiLocationNode[], depth: number, rootId: string | null) => {
    for (const item of items) {
      const effectiveRootId = depth === 0 ? item.id : rootId ?? item.id;
      const option: LocationOption = {
        id: item.id,
        rootId: effectiveRootId,
        code: item.code,
        name: item.name,
        label: depth === 0 ? formatLocationLabel(item) : `${'  '.repeat(depth - 1)}- ${formatLocationLabel(item)}`,
        depth,
      };

      locationById.set(item.id, option);
      if (depth === 0) {
        rootOptions.push(option);
      } else {
        childOptions.push(option);
      }

      walk(item.children, depth + 1, effectiveRootId);
    }
  };

  walk(nodes, 0, null);
  return { rootOptions, childOptions, locationById };
}

function renderSessionStatus(status: 'completed', t: (key: string) => string) {
  if (status === 'completed') {
    return t('inventory.sessionStatus.completed');
  }

  return status;
}

export default function InventoryPage() {
  const { t, language } = useI18n();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const scannerInputRef = useRef<HTMLInputElement | null>(null);
  const cameraVideoRef = useRef<HTMLVideoElement | null>(null);
  const cameraControlsRef = useRef<IScannerControls | null>(null);
  const cameraReaderRef = useRef<BrowserMultiFormatReader | null>(null);
  const handleScanRef = useRef<(rawBarcode: string) => Promise<void>>(async () => undefined);
  const [searchParams] = useSearchParams();
  const {
    activeSession,
    completedSessions,
    startSession,
    resetActiveSession,
    registerUnknownScan,
    registerScan,
    completeSession,
  } = useInventoryDemoState();

  const [selectedRootId, setSelectedRootId] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [barcodeInput, setBarcodeInput] = useState('');
  const [completedSummary, setCompletedSummary] = useState<CompletedInventorySession | null>(null);
  const [rootOptions, setRootOptions] = useState<LocationOption[]>([]);
  const [childOptions, setChildOptions] = useState<LocationOption[]>([]);
  const [locationById, setLocationById] = useState<Map<string, LocationOption>>(new Map());
  const [allVariboxes, setAllVariboxes] = useState<VariboxViewRecord[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraMode, setCameraMode] = useState<CameraScanMode>('barcode');
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState('');

  const availableLocations = rootOptions;
  const availableOptions = useMemo(() => {
    if (!selectedRootId) {
      return [];
    }

    return childOptions.filter((option) => option.rootId === selectedRootId);
  }, [childOptions, selectedRootId]);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setLoadingData(true);
      setErrorMessage('');

      try {
        const [locationTreeResponse, variboxesResponse] = await Promise.all([
          getLocationTree(),
          listVariboxes({ limit: 200, offset: 0 }),
        ]);

        if (cancelled) {
          return;
        }

        const flattened = flattenLocations(locationTreeResponse.items);
        setRootOptions(flattened.rootOptions);
        setChildOptions(flattened.childOptions);
        setLocationById(flattened.locationById);
        setAllVariboxes(variboxesResponse.items.map(mapApiVariboxToView));
      } catch {
        if (!cancelled) {
          setErrorMessage(t('inventory.apiError'));
        }
      } finally {
        if (!cancelled) {
          setLoadingData(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    const code = searchParams.get('code');
    if (!code || !rootOptions.length) {
      return;
    }

    const match = rootOptions.find((root) => root.code?.toUpperCase() === code.toUpperCase());
    if (match) {
      setSelectedRootId(match.id);
      setSelectedLocationId('');
    }
  }, [rootOptions, searchParams]);

  useEffect(() => {
    if (!rootOptions.length) {
      return;
    }

    if (selectedRootId) {
      const exists = rootOptions.some((root) => root.id === selectedRootId);
      if (exists) {
        return;
      }
    }

    const preferred = rootOptions.find((root) => root.code?.toUpperCase() === 'PL01') ?? rootOptions[0];
    setSelectedRootId(preferred.id);
    setSelectedLocationId('');
  }, [rootOptions, selectedRootId]);

  useEffect(() => {
    if (activeSession) {
      scannerInputRef.current?.focus();
    }
  }, [activeSession, barcodeInput]);

  const selectedLocation = useMemo<InventoryLocationSelection | null>(() => {
    const locationId = selectedLocationId || (selectedRootId && availableOptions.length === 0 ? selectedRootId : '');
    if (!selectedRootId || !locationId) {
      return null;
    }

    return {
      rootId: selectedRootId,
      locationId,
    };
  }, [availableOptions.length, selectedLocationId, selectedRootId]);

  const selectedLocationLabel = useMemo(() => {
    if (!selectedLocation) {
      return t('inventory.noLocationSelected');
    }

    return locationById.get(selectedLocation.locationId)?.label ?? t('inventory.noLocationSelected');
  }, [locationById, selectedLocation, t]);

  const expectedVariboxes = useMemo<InventoryVariboxSnapshot[]>(() => {
    if (!selectedLocation) {
      return [];
    }

    return allVariboxes
      .filter((record) => record.currentLocationId === selectedLocation.locationId)
      .map((record) => ({
        serialNumber: record.serialNumber,
        radexId: record.radexId,
        status: record.status,
        location: {
          rootId: selectedLocation.rootId,
          locationId: selectedLocation.locationId,
        },
        currentLocationLabel: selectedLocationLabel,
        lastMovement: record.lastMovement,
      }));
  }, [allVariboxes, selectedLocation, selectedLocationLabel]);

  const expectedCount = activeSession ? activeSession.expected.length : expectedVariboxes.length;
  const foundCount = activeSession ? activeSession.found.length : 0;
  const unexpectedCount = activeSession ? activeSession.unexpected.length : 0;
  const scannedCount = activeSession ? activeSession.found.length + activeSession.unexpected.length : 0;
  const missingCount = Math.max(0, expectedCount - foundCount);
  const progress = expectedCount > 0 ? Math.round((scannedCount / expectedCount) * 100) : 0;

  const inventoryLocationLabel = useMemo(() => {
    if (activeSession) {
      return activeSession.locationLabel;
    }

    return selectedLocationLabel;
  }, [activeSession, selectedLocationLabel]);

  const focusScanner = () => {
    window.setTimeout(() => {
      scannerInputRef.current?.focus();
      scannerInputRef.current?.select();
    }, 0);
  };

  const closeCameraScanner = () => {
    cameraControlsRef.current?.stop();
    cameraControlsRef.current = null;
    cameraReaderRef.current = null;
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }
    setCameraOpen(false);
    setCameraLoading(false);
  };

  const openCameraScanner = () => {
    setCameraError('');
    setCameraOpen(true);
  };

  useEffect(() => {
    if (!cameraOpen || !activeSession) {
      return;
    }

    let cancelled = false;

    const stopCamera = () => {
      cameraControlsRef.current?.stop();
      cameraControlsRef.current = null;
      cameraReaderRef.current = null;
      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = null;
      }
    };

    const startCamera = async () => {
      setCameraLoading(true);
      setCameraError('');

      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('unsupported');
        }

        const video = cameraVideoRef.current;
        if (!video) {
          throw new Error('video-not-ready');
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        video.srcObject = stream;
        await video.play();

        const hints = new Map<DecodeHintType, unknown>();
        hints.set(
          DecodeHintType.POSSIBLE_FORMATS,
          cameraMode === 'qr' ? [BarcodeFormat.QR_CODE] : ONE_DIMENSIONAL_FORMATS,
        );
        hints.set(DecodeHintType.TRY_HARDER, true);

        const reader = new BrowserMultiFormatReader(hints);
        cameraReaderRef.current = reader;
        const controls = await reader.decodeFromVideoElement(video, (result) => {
          if (!result) {
            return;
          }

          const format = result.getBarcodeFormat();
          if (cameraMode === 'qr' ? format !== BarcodeFormat.QR_CODE : format === BarcodeFormat.QR_CODE) {
            return;
          }

          const value = result.getText().trim();
          if (!value) {
            return;
          }

          closeCameraScanner();
          void handleScanRef.current(value);
        });
        cameraControlsRef.current = controls;
      } catch {
        if (!cancelled) {
          setCameraError(t('inventory.camera.unavailable'));
        }
      } finally {
        if (!cancelled) {
          setCameraLoading(false);
        }
      }
    };

    void startCamera();
    return () => {
      cancelled = true;
      stopCamera();
      setCameraLoading(false);
    };
  }, [activeSession, cameraMode, cameraOpen, t]);

  useEffect(() => {
    if (!activeSession && cameraOpen) {
      closeCameraScanner();
    }
  }, [activeSession, cameraOpen]);

  useEffect(() => {
    return () => {
      cameraControlsRef.current?.stop();
      cameraControlsRef.current = null;
      cameraReaderRef.current = null;
    };
  }, []);

  const startInventorySession = () => {
    if (!selectedLocation) {
      return;
    }

    startSession({
      location: selectedLocation,
      locationLabel: selectedLocationLabel,
      expected: expectedVariboxes,
      user: 'Anna Kowalska',
    });

    setCompletedSummary(null);
    setBarcodeInput('');
    focusScanner();
  };

  const handleScan = async (rawBarcode: string) => {
    if (!activeSession) {
      return;
    }

    const barcode = rawBarcode.trim();
    if (!barcode) {
      return;
    }

    try {
      const record = await findVariboxByBarcode(barcode);

      if (!record) {
        registerUnknownScan(barcode);
        setBarcodeInput('');
        focusScanner();
        return;
      }

      const currentLocationId = record.currentLocationId;
      const currentLocationOption = currentLocationId ? locationById.get(currentLocationId) : undefined;
      const currentLocation: InventoryLocationSelection = {
        rootId: currentLocationOption?.rootId ?? 'unassigned',
        locationId: currentLocationId ?? 'unassigned',
      };
      const currentLocationLabel = currentLocationOption?.label ?? t('assets.variboxDetails.noClient');

      registerScan({
        serialNumber: record.serialNumber,
        radexId: record.radexId,
        status: record.status,
        currentLocation,
        currentLocationLabel,
      });
    } catch {
      registerUnknownScan(barcode);
    }

    setBarcodeInput('');
    focusScanner();
  };

  handleScanRef.current = handleScan;

  const submitScan = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleScan(barcodeInput);
  };

  const finishSession = () => {
    const completed = completeSession();
    if (completed) {
      setCompletedSummary(completed);
    }
  };

  const currentResult = activeSession?.lastScanResult ?? null;

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow={t('inventory.eyebrow')}
        title={t('inventory.sessionTitle')}
        description={t('inventory.sessionDescription')}
      />

      {!activeSession && !completedSummary ? (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('inventory.startTitle')}</Typography>

            {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
            {loadingData ? <LinearProgress sx={{ borderRadius: 999 }} /> : null}

            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label={t('inventory.locationCodeLabel')}
                  value={selectedRootId}
                  onChange={(event) => {
                    setSelectedRootId(event.target.value);
                    setSelectedLocationId('');
                  }}
                  disabled={loadingData || !!errorMessage}
                >
                  <MenuItem value="">{t('inventory.selectLocationCode')}</MenuItem>
                  {availableLocations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>{location.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label={t('inventory.locationOptionLabel')}
                  value={selectedLocationId}
                  onChange={(event) => setSelectedLocationId(event.target.value)}
                  disabled={!selectedRootId || loadingData || !!errorMessage}
                >
                  <MenuItem value="">{t('inventory.selectLocationOption')}</MenuItem>
                  {availableOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {selectedRootId && availableOptions.length === 0 ? (
              <Alert severity="info">{t('inventory.rootLocationFallback')}</Alert>
            ) : null}

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle1">{t('inventory.locationLabel')}: {inventoryLocationLabel}</Typography>
                <Typography variant="body2">{t('inventory.counters.expected')}: {expectedVariboxes.length}</Typography>
                <Typography variant="body2">{t('inventory.counters.scanned')}: 0</Typography>
                <Typography variant="body2">{t('inventory.counters.found')}: 0</Typography>
                <Typography variant="body2">{t('inventory.counters.unexpected')}: 0</Typography>
                <Typography variant="body2">{t('inventory.counters.missing')}: {expectedVariboxes.length}</Typography>
              </Stack>
            </Paper>

            <Button
              variant="contained"
              size="large"
              onClick={startInventorySession}
              disabled={!selectedLocation || loadingData || !!errorMessage}
              sx={{ minHeight: 56, fontWeight: 800 }}
            >
              {t('inventory.buttons.startScanning')}
            </Button>
          </Stack>
        </Paper>
      ) : null}

      {activeSession ? (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('inventory.activeSessionTitle')}</Typography>
            <Typography variant="subtitle1">{t('inventory.locationLabel')}: {activeSession.locationLabel}</Typography>

            <Stack spacing={1}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{scannedCount} / {expectedCount}</Typography>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 999 }} />
            </Stack>

            <Grid container spacing={1.25}>
              <Grid item xs={6} md={4} lg={2}><Chip label={`${t('inventory.counters.expected')}: ${expectedCount}`} /></Grid>
              <Grid item xs={6} md={4} lg={2}><Chip label={`${t('inventory.counters.scanned')}: ${scannedCount}`} /></Grid>
              <Grid item xs={6} md={4} lg={2}><Chip label={`${t('inventory.counters.found')}: ${foundCount}`} color="success" /></Grid>
              <Grid item xs={6} md={4} lg={2}><Chip label={`${t('inventory.counters.unexpected')}: ${unexpectedCount}`} color="warning" /></Grid>
              <Grid item xs={6} md={4} lg={2}><Chip label={`${t('inventory.counters.missing')}: ${missingCount}`} color="error" /></Grid>
            </Grid>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.25}>
                <Typography variant="subtitle2">{t('inventory.scannerTitle')}</Typography>
                <Box component="form" onSubmit={submitScan}>
                  <Stack spacing={1.25}>
                    <TextField
                      inputRef={scannerInputRef}
                      fullWidth
                      label={t('inventory.barcodeLabel')}
                      placeholder={t('inventory.barcodePlaceholder')}
                      value={barcodeInput}
                      onChange={(event) => setBarcodeInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleScan(barcodeInput);
                        }
                      }}
                      InputProps={{
                        sx: {
                          fontSize: isMobile ? 24 : 20,
                          fontWeight: 800,
                          letterSpacing: 1.2,
                        },
                      }}
                    />
                    <Button type="submit" variant="contained" size="large" sx={{ minHeight: 56, fontWeight: 800 }}>
                      {isMobile ? t('inventory.buttons.scanVariboxMobile') : t('inventory.buttons.scanNext')}
                    </Button>
                    <Button variant="outlined" size="large" onClick={openCameraScanner} sx={{ minHeight: 52 }}>
                      {t('inventory.buttons.scanWithCamera')}
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            </Paper>

            {currentResult ? (
              <Alert severity={currentResult.type === 'found' ? 'success' : currentResult.type === 'unexpected' ? 'warning' : currentResult.type === 'duplicate' ? 'info' : 'error'}>
                {currentResult.type === 'found' ? (
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2">{t('inventory.results.foundTitle')}</Typography>
                    <Typography variant="body2">{currentResult.serialNumber} · {currentResult.radexId}</Typography>
                    <Typography variant="body2">{t('inventory.currentLocationLabel')}: {currentResult.currentLocationLabel}</Typography>
                    <Typography variant="body2">{t('assets.headings.status')}: {t(`assets.statuses.${currentResult.status}`)}</Typography>
                  </Stack>
                ) : null}

                {currentResult.type === 'unexpected' ? (
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2">{t('inventory.results.unexpectedTitle')}</Typography>
                    <Typography variant="body2">{t('inventory.results.unexpectedDescription')}</Typography>
                    <Typography variant="body2">{currentResult.serialNumber} · {currentResult.radexId}</Typography>
                    <Typography variant="body2">{t('inventory.currentLocationLabel')}: {currentResult.currentLocationLabel}</Typography>
                    <Typography variant="body2">{t('inventory.inventoryLocationLabel')}: {currentResult.inventoryLocationLabel}</Typography>
                  </Stack>
                ) : null}

                {currentResult.type === 'duplicate' ? (
                  <Typography variant="body2">{t('inventory.results.duplicate')}: {currentResult.serialNumber} · {currentResult.radexId}</Typography>
                ) : null}

                {currentResult.type === 'unknown' ? (
                  <Typography variant="body2">{t('inventory.results.unknown')}: {currentResult.barcode}</Typography>
                ) : null}
              </Alert>
            ) : null}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <Button variant="outlined" onClick={focusScanner}>{t('inventory.buttons.scanNext')}</Button>
              <Button variant="contained" color="error" onClick={finishSession}>{t('inventory.buttons.finishInventory')}</Button>
            </Stack>
          </Stack>
        </Paper>
      ) : null}

      {completedSummary ? (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('inventory.summary.title')}</Typography>
            <Typography variant="subtitle1">{t('inventory.locationLabel')}: {completedSummary.locationLabel}</Typography>

            <Grid container spacing={1.25}>
              <Grid item xs={6} md={3}><Chip label={`${t('inventory.counters.expected')}: ${completedSummary.expectedCount}`} /></Grid>
              <Grid item xs={6} md={3}><Chip label={`${t('inventory.counters.found')}: ${completedSummary.foundCount}`} color="success" /></Grid>
              <Grid item xs={6} md={3}><Chip label={`${t('inventory.counters.unexpected')}: ${completedSummary.unexpectedCount}`} color="warning" /></Grid>
              <Grid item xs={6} md={3}><Chip label={`${t('inventory.counters.missing')}: ${completedSummary.missingCount}`} color="error" /></Grid>
            </Grid>

            {!isMobile ? (
              <Grid container spacing={2}>
                <Grid item xs={12} lg={4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('inventory.summary.foundList')}</Typography>
                    {completedSummary.found.map((item) => (
                      <Typography key={item.serialNumber} variant="body2">{item.serialNumber} · {item.radexId}</Typography>
                    ))}
                  </Paper>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('inventory.summary.unexpectedList')}</Typography>
                    {completedSummary.unexpected.map((item) => (
                      <Typography key={item.serialNumber} variant="body2">{item.serialNumber} · {item.radexId} · {item.currentLocationLabel}</Typography>
                    ))}
                  </Paper>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('inventory.summary.missingList')}</Typography>
                    {completedSummary.missing.map((item) => (
                      <Typography key={item.serialNumber} variant="body2">{item.serialNumber} · {item.radexId} · {item.currentLocationLabel} · {t(`assets.statuses.${item.status}`)}</Typography>
                    ))}
                  </Paper>
                </Grid>
              </Grid>
            ) : (
              <Stack spacing={1}>
                <Typography variant="body2">{t('inventory.summary.foundList')}: {completedSummary.foundCount}</Typography>
                <Typography variant="body2">{t('inventory.summary.unexpectedList')}: {completedSummary.unexpectedCount}</Typography>
                <Typography variant="body2">{t('inventory.summary.missingList')}: {completedSummary.missingCount}</Typography>
              </Stack>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <Button variant="contained" onClick={() => setCompletedSummary(null)}>{t('inventory.buttons.newSession')}</Button>
              <Button variant="outlined" onClick={resetActiveSession}>{t('inventory.buttons.closeSession')}</Button>
            </Stack>
          </Stack>
        </Paper>
      ) : null}

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={1.5}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('inventory.historyTitle')}</Typography>

          {completedSessions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{t('inventory.historyEmpty')}</Typography>
          ) : null}

          {completedSessions.length > 0 && !isMobile ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('inventory.history.location')}</TableCell>
                    <TableCell>{t('inventory.history.date')}</TableCell>
                    <TableCell>{t('inventory.history.progress')}</TableCell>
                    <TableCell>{t('inventory.history.missing')}</TableCell>
                    <TableCell>{t('inventory.history.status')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {completedSessions.map((session) => (
                    <TableRow key={session.id}>
                      <TableCell>{session.locationLabel}</TableCell>
                      <TableCell>{new Date(session.endAtIso).toLocaleDateString(language === 'pl' ? 'pl-PL' : 'en-GB')}</TableCell>
                      <TableCell>{session.foundCount} / {session.expectedCount}</TableCell>
                      <TableCell>{session.missingCount} {t('inventory.history.missingSuffix')}</TableCell>
                      <TableCell>{renderSessionStatus(session.status, t)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}

          {completedSessions.length > 0 && isMobile ? (
            <Stack spacing={1}>
              {completedSessions.slice(0, 5).map((session) => (
                <Paper key={session.id} variant="outlined" sx={{ p: 1.25 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{session.locationLabel}</Typography>
                  <Typography variant="caption" color="text.secondary">{new Date(session.endAtIso).toLocaleDateString(language === 'pl' ? 'pl-PL' : 'en-GB')}</Typography>
                  <Typography variant="body2">{session.foundCount} / {session.expectedCount}</Typography>
                  <Typography variant="body2">{session.missingCount} {t('inventory.history.missingSuffix')}</Typography>
                </Paper>
              ))}
            </Stack>
          ) : null}

          <Dialog open={cameraOpen} onClose={closeCameraScanner} fullScreen={isMobile} fullWidth maxWidth="sm">
            <DialogTitle>{t('inventory.camera.title')}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t(cameraMode === 'barcode' ? 'inventory.camera.barcodeDescription' : 'inventory.camera.qrDescription')}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  color="primary"
                  value={cameraMode}
                  onChange={(_, nextMode: CameraScanMode | null) => {
                    if (nextMode) {
                      setCameraMode(nextMode);
                    }
                  }}
                  aria-label={t('inventory.camera.modeLabel')}
                >
                  <ToggleButton value="barcode">{t('inventory.camera.barcodeMode')}</ToggleButton>
                  <ToggleButton value="qr">{t('inventory.camera.qrMode')}</ToggleButton>
                </ToggleButtonGroup>
                <Paper variant="outlined" sx={{ position: 'relative', overflow: 'hidden', borderRadius: 2, minHeight: isMobile ? '58vh' : 280, bgcolor: 'rgba(15,23,42,0.92)' }}>
                  <Box
                    component="video"
                    ref={cameraVideoRef}
                    muted
                    playsInline
                    autoPlay
                    sx={{ display: 'block', width: '100%', height: isMobile ? '68vh' : 320, objectFit: 'cover' }}
                  />
                  {cameraLoading ? (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#fff', bgcolor: 'rgba(15,23,42,0.55)' }}>
                      <Stack alignItems="center" spacing={1}>
                        <CircularProgress color="inherit" size={28} />
                        <Typography variant="body2">{t('inventory.camera.loading')}</Typography>
                      </Stack>
                    </Box>
                  ) : null}
                </Paper>
                {cameraError ? <Alert severity="warning">{cameraError}</Alert> : null}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeCameraScanner}>{t('inventory.camera.close')}</Button>
            </DialogActions>
          </Dialog>
        </Stack>
      </Paper>
    </Stack>
  );
}
